import React, { useMemo, useState, useEffect } from 'react';
import type { DailyData } from '../../types/index';
import type { TimeRange } from '../../types/dashboard';
import {
  getDashboardWidgetTimeframe,
  getWidgetTimeframeTrailingDays,
  type WidgetTimeframeToken,
} from '../../lib/dashboardTimeRange';
import { getTradePnl, getTradeTimestampMs, normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';
import { filterTradesByWidgetTimeframe } from '../../lib/trades/tradeLevelStats';

interface TotalGainLossWidgetProps {
  dailyData?: DailyData[];
  trades?: any[];
  timeRange?: TimeRange;
}

const Icon: React.FC<{ size?: number; className?: string; children?: React.ReactNode }> = ({ size = 20, className, children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    {children}
  </svg>
);

const ArrowUp = ({ size = 12 }: { size?: number }) => (
  <Icon size={size}><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></Icon>
);
const ArrowDown = ({ size = 12 }: { size?: number }) => (
  <Icon size={size}><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></Icon>
);

export const TotalGainLossWidget: React.FC<TotalGainLossWidgetProps> = ({ dailyData = [], trades, timeRange }) => {
  const [timeframe, setTimeframe] = useState<WidgetTimeframeToken>('7D');

  useEffect(() => {
    if (timeRange) setTimeframe(getDashboardWidgetTimeframe(timeRange));
  }, [timeRange]);

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent<{ id?: string; value?: unknown }>).detail;
        if (!detail || detail.id !== 'totalGainLoss') return;
        const val = detail.value as WidgetTimeframeToken | string;
        if (val === '24H' || val === '7D' || val === '30D' || val === '12M' || val === 'YTD') setTimeframe(val);
      } catch {}
    };
    window.addEventListener('widget-timeframe:selected', handler as EventListener);
    return () => window.removeEventListener('widget-timeframe:selected', handler as EventListener);
  }, []);

  const now = Date.now();
  const tfDays = getWidgetTimeframeTrailingDays(timeframe);
  const tfMs = tfDays * 24 * 60 * 60 * 1000;

  const hasTrades = Array.isArray(trades) && trades.length > 0;

  // Canonical source of truth: when the authoritative trade records are provided
  // (DashboardPage passes the canonical collection), the total comes from the
  // individual closed trades through the SAME resolver (getTradePnl) used by the
  // dashboard daily aggregation and every other widget. dailyData remains the
  // fallback for callers that only pass daily buckets.
  const currentPeriodTrades = useMemo(() => {
    if (!hasTrades) return null;
    return filterTradesByWidgetTimeframe(trades as any[], timeframe);
  }, [trades, timeframe, hasTrades]);

  const prevPeriodTrades = useMemo(() => {
    if (!hasTrades || timeframe === 'YTD') return null;
    const start = now - 2 * tfMs;
    const end = now - tfMs;
    return (trades as any[]).filter((trade) => {
      if (normalizeTradeStatus(trade) !== 'closed') return false;
      const ts = getTradeTimestampMs(trade);
      return ts != null && ts >= start && ts < end;
    });
  }, [trades, timeframe, hasTrades, now, tfMs]);

  const hasDate = dailyData.some(d => Boolean(d.date) || Boolean((d as { t?: unknown }).t) || Boolean((d as { timestamp?: unknown }).timestamp));

  const currentPeriod = useMemo(() => {
    if (!hasDate) return dailyData.slice(-Math.min(dailyData.length, tfDays));
    const start = timeframe === 'YTD' ? new Date(new Date().getFullYear(),0,1).getTime() : (now - tfMs);
    return dailyData.filter(d => {
      const raw = d.date ?? (d as { t?: unknown }).t ?? (d as { timestamp?: unknown }).timestamp;
      const ts = typeof raw === 'number' ? raw : (typeof raw === 'string' ? Date.parse(raw) : NaN);
      if (!Number.isFinite(ts)) return false;
      if (timeframe === 'YTD') return ts >= start;
      return ts >= start;
    });
  }, [dailyData, timeframe, hasDate, now, tfDays, tfMs]);

  const prevPeriod = useMemo(() => {
    if (!hasDate) return [] as DailyData[];
    if (timeframe === 'YTD') return [] as DailyData[];
    const start = now - 2 * tfMs;
    const end = now - tfMs;
    return dailyData.filter(d => {
      const raw = d.date ?? (d as { t?: unknown }).t ?? (d as { timestamp?: unknown }).timestamp;
      const ts = typeof raw === 'number' ? raw : (typeof raw === 'string' ? Date.parse(raw) : NaN);
      if (!Number.isFinite(ts)) return false;
      return ts >= start && ts < end;
    });
  }, [dailyData, timeframe, hasDate, now, tfMs]);

  const totalPnl = useMemo(() => {
    if (currentPeriodTrades) {
      return currentPeriodTrades.reduce((acc, trade) => acc + getTradePnl(trade), 0);
    }
    return currentPeriod.reduce((acc, d) => acc + (d.pnl || 0), 0);
  }, [currentPeriodTrades, currentPeriod]);
  const prevPnl = useMemo(() => {
    if (prevPeriodTrades) {
      return prevPeriodTrades.reduce((acc, trade) => acc + getTradePnl(trade), 0);
    }
    return prevPeriod.reduce((acc, d) => acc + (d.pnl || 0), 0);
  }, [prevPeriodTrades, prevPeriod]);

  const hasPreviousPeriodData = prevPeriodTrades !== null
    ? prevPeriodTrades.length > 0
    : prevPeriod.some((day) => Number((day as any).trades ?? 0) > 0 || Number(day.pnl ?? 0) !== 0);

  const change = hasPreviousPeriodData && prevPnl !== 0
    ? ((totalPnl - prevPnl) / Math.abs(prevPnl)) * 100
    : null;
  const isPositive = totalPnl >= 0;
  const isTrendPositive = change != null && change >= 0;

  const colorClass = isPositive ? 'text-emerald-600' : 'text-red-500';
  const trendColorClass = change == null ? 'text-zinc-500 dark:text-zinc-400' : isTrendPositive ? 'text-emerald-600' : 'text-red-500';
  const pillClasses = isTrendPositive
    ? 'px-2 py-0.5 text-xs rounded-md font-semibold border bg-emerald-600/10 text-emerald-600 border-emerald-600/40'
    : 'px-2 py-0.5 text-xs rounded-md font-semibold border bg-red-500/10 text-red-500 border-red-500/40';

  return (
    <div className="relative group h-full w-full flex flex-col items-center justify-start px-2 pb-1 pt-0">
      {/* top-right action icons removed per request */}

      <div className="relative z-10 -mt-2 flex max-w-full flex-col items-center justify-start">
        <div
          data-testid="total-gain-loss-amount"
          className={`max-w-full text-5xl sm:text-5xl md:text-5xl font-bold tracking-tight ${colorClass} tabular-nums`}
        >
          {totalPnl < 0 ? '-' : ''}${Math.abs(totalPnl).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </div>

        <div className="mt-2 max-w-full">
          <span
            data-testid="total-gain-loss-comparison"
            className={`${pillClasses} inline-flex max-w-full items-center gap-1 whitespace-nowrap`}
          >
            {change == null ? (
              <span className={trendColorClass}>{hasPreviousPeriodData ? 'N/A vs previous period' : 'No prior-period data'}</span>
            ) : (
              <>
                <span className={`${trendColorClass} flex items-center`}>
                  {isTrendPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                </span>
                <span className={trendColorClass}>{Math.abs(change).toFixed(1)}%</span>
                <span className="text-[11px] text-zinc-800 dark:text-gray-400">vs previous period</span>
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
